import { create } from 'zustand'
import type { User } from '@/types/api'
import type { ProfileTab } from '@/components/profile/ProfileCard'

/**
 * Цель всплывашки о пользователе: кто, где (сервер) и координаты курсора.
 * mode — что показать: 'card' по левому клику (карточка), 'menu' по правому (действия),
 * 'profile' — сразу большое окно профиля (пункт «Профиль», переход по общим друзьям).
 */
export interface UserMenuTarget {
  userId: string
  user?: User | undefined
  guildId?: string | undefined
  /** Меню открыто на строке личной беседы: сверху — «прочитано» и mute этой беседы. */
  dmChannelId?: string | undefined
  mode: 'card' | 'menu' | 'profile'
  /** Большое окно профиля — сразу на этой вкладке (общие друзья, серверы). */
  tab?: ProfileTab | undefined
  x: number
  y: number
}

interface ContextMenuState {
  target: UserMenuTarget | null
  openUserMenu: (target: UserMenuTarget) => void
  close: () => void
}

export const useContextMenuStore = create<ContextMenuState>((set) => ({
  target: null,
  openUserMenu: (target) => set({ target }),
  close: () => set({ target: null }),
}))

type Opts = { user?: User | undefined; guildId?: string | undefined; dmChannelId?: string | undefined }

function handler(mode: UserMenuTarget['mode'], userId: string, opts: Opts) {
  return (e: { preventDefault: () => void; clientX: number; clientY: number }) => {
    e.preventDefault()
    useContextMenuStore.getState().openUserMenu({ userId, user: opts.user, guildId: opts.guildId, dmChannelId: opts.dmChannelId, mode, x: e.clientX, y: e.clientY })
  }
}

/** Правый клик по пользователю: список действий. */
export function userContextHandler(userId: string, opts: Opts = {}) {
  return handler('menu', userId, opts)
}

/** Левый клик по имени, упоминанию или строке участника: карточка профиля. */
export function userCardHandler(userId: string, opts: Opts = {}) {
  return handler('card', userId, opts)
}

/** Открыть большое окно профиля: из карточки, из общих друзей и из панели профиля в беседе. */
export function openProfile(userId: string, opts: Opts & { tab?: ProfileTab | undefined } = {}) {
  useContextMenuStore.getState().openUserMenu({ userId, user: opts.user, guildId: opts.guildId, mode: 'profile', tab: opts.tab, x: 0, y: 0 })
}
